<template>
  <div class="roi-audit-page">
    <div class="filter-bar">
      <el-form
        ref="searchForm"
        :model="searchForm"
        label-width="80px"
        class="filter-form"
      >
        <el-row :gutter="16" class="filter-grid">
          <el-col :xs="24" :sm="24" :md="12" :lg="6" :xl="6">
            <el-form-item label="统计类别">
              <el-radio-group v-model="searchForm.statType" class="filter-stat-radios">
                <el-radio-button label="year">按年</el-radio-button>
                <el-radio-button label="month">按月</el-radio-button>
                <el-radio-button label="week">按周</el-radio-button>
                <el-radio-button label="day">按日</el-radio-button>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="12" :lg="5" :xl="5">
            <el-form-item label="统计日期">
              <el-date-picker
                :key="'roi-audit-dp-' + searchForm.statType"
                :value="pickerDateValue"
                :type="pickerDateType"
                :format="pickerDateFormat"
                :value-format="pickerDateValueFormat"
                :placeholder="pickerDatePlaceholder"
                :picker-options="pickerDateOptions"
                append-to-body
                class="filter-date-picker"
                @input="handlePickerDateInput"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="12" :lg="6" :xl="6">
            <el-form-item label="机构品牌">
              <utwl-org-select
                v-model="searchForm.orgIds"
                placeholder="请选择（可多选）"
                multiple
                collapse-tags
                width="100%"
                :page-size="20"
                search-param="orgName"
                select-class="filter-org-select"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="24" :md="12" :lg="7" :xl="7">
            <el-form-item label="" class="filter-actions-item">
              <div class="filter-actions">
                <el-button type="primary" icon="el-icon-search" @click="handleQuery" :loading="queryLoading">查询</el-button>
                <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
                <el-button icon="el-icon-download" @click="handleExport" :loading="exportLoading">导出</el-button>
                <el-popover placement="bottom" trigger="hover" width="400">
                  <div class="roi-help-content">
                    <p>1、资源量 = 推送给当前机构的学生总数</p>
                    <p>2、核对订单总数 = 推送给当前机构的已报名的推送条数</p>
                    <p>3、报名率 = 核对订单总数 / 资源总量</p>
                    <p>4、成交总额 = 推送给当前机构的全部已报名的成交金额</p>
                    <p>5、结算总额 = 推送给当前机构的全部已报名的返佣总和</p>
                    <p>6、单笔 = 结算总额 / 核对订单总数</p>
                    <p>7、渠道ROI = 渠道结算总额 / 渠道消费总额 - 1</p>
                    <p>8、总ROI = 结算总额 / 消费总额 - 1（结算总额、消费总额为 0 时显示“-”）</p>
                  </div>
                  <i slot="reference" class="el-icon-question roi-help-icon" />
                </el-popover>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>
    <el-card class="table-card" shadow="never">
      <el-table
        ref="roiAuditTable"
        :key="tableRenderKey"
        :data="pagedTableData"
        border
        stripe
        size="small"
        v-loading="loading"
        height="100%"
        class="roi-audit-table"
        :span-method="orgNameSpanMethod"
        :header-cell-style="getHeaderCellStyle"
        :header-cell-class-name="getHeaderCellClassName"
        :cell-class-name="getCellClassName"
        :cell-style="getCellStyle"
      >
        <el-table-column type="index" label="序号" width="50" align="center" fixed="left" :index="indexMethod" />
        <el-table-column prop="orgName" label="机构品牌" min-width="140" align="center" fixed="left" class-name="org-name-column">
          <template slot-scope="{ row }">
            <span class="org-name-text">{{ row.orgName || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="promotionId" label="推广ID" width="120" align="center" show-overflow-tooltip fixed="left" />
        <el-table-column prop="cityName" label="城市名称" min-width="110" align="center" show-overflow-tooltip fixed="left" />
        <el-table-column prop="consumption" width="100" align="center">
          <template slot="header">
            <div class="roi-total-header">
              <span>消费总额</span>
              <span class="roi-total-value">{{ formatNum(auditTotals.consumption) }}</span>
            </div>
          </template>
          <template slot-scope="{ row }">{{ formatNum(row.consumption) }}</template>
        </el-table-column>
        <el-table-column prop="resourceCount" width="100" align="center">
          <template slot="header">
            <div class="roi-total-header">
              <span>资源总量</span>
              <span class="roi-total-value">{{ formatNum(auditTotals.resourceCount) }}</span>
            </div>
          </template>
          <template slot-scope="{ row }">{{ formatNum(row.resourceCount) }}</template>
        </el-table-column>
        <el-table-column prop="verifyCount" width="130" align="center">
          <template slot="header">
            <div class="roi-total-header">
              <span>核对订单总数</span>
              <span class="roi-total-value">{{ formatNum(auditTotals.verifyCount) }}</span>
            </div>
          </template>
          <template slot-scope="{ row }">{{ formatNum(row.verifyCount) }}</template>
        </el-table-column>
        <el-table-column prop="enrollRate" label="报名率" width="90" align="center" />
        <el-table-column prop="dealAmount" width="100" align="center">
          <template slot="header">
            <div class="roi-total-header">
              <span>成交总额</span>
              <span class="roi-total-value">{{ formatNum(auditTotals.dealAmount) }}</span>
            </div>
          </template>
          <template slot-scope="{ row }">{{ formatNum(row.dealAmount) }}</template>
        </el-table-column>
        <el-table-column prop="settlementAmount" width="100" align="center">
          <template slot="header">
            <div class="roi-total-header">
              <span>结算总额</span>
              <span class="roi-total-value">{{ formatNum(auditTotals.settlementAmount) }}</span>
            </div>
          </template>
          <template slot-scope="{ row }">{{ formatNum(row.settlementAmount) }}</template>
        </el-table-column>
        <el-table-column prop="perOrder" label="单笔" width="90" align="center">
          <template slot-scope="{ row }">{{ formatNum(row.perOrder) }}</template>
        </el-table-column>
        <el-table-column prop="totalPromotionRoi" label="总ROI" width="90" align="center">
          <template slot-scope="{ row }">{{ formatNum(row.totalPromotionRoi) }}</template>
        </el-table-column>
        <el-table-column label="百度推广" align="center">
          <el-table-column prop="baiduConsumption" label="渠道消费总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.baiduConsumption) }}</template>
          </el-table-column>
          <el-table-column prop="baiduResource" label="渠道资源总量" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.baiduResource) }}</template>
          </el-table-column>
          <el-table-column prop="baiduVerify" label="渠道核对订单总数" width="150" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.baiduVerify) }}</template>
          </el-table-column>
          <el-table-column prop="baiduDealAmount" label="渠道成交总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.baiduDealAmount) }}</template>
          </el-table-column>
          <el-table-column prop="baiduSettlement" label="渠道结算总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.baiduSettlement) }}</template>
          </el-table-column>
          <el-table-column prop="baiduRoi" label="渠道ROI" width="80" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.baiduRoi) }}</template>
          </el-table-column>
        </el-table-column>
        <el-table-column label="抖音推广" align="center">
          <el-table-column prop="douyinConsumption" label="渠道消费总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.douyinConsumption) }}</template>
          </el-table-column>
          <el-table-column prop="douyinResource" label="渠道资源总量" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.douyinResource) }}</template>
          </el-table-column>
          <el-table-column prop="douyinVerify" label="渠道核对订单总数" width="150" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.douyinVerify) }}</template>
          </el-table-column>
          <el-table-column prop="douyinDealAmount" label="渠道成交总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.douyinDealAmount) }}</template>
          </el-table-column>
          <el-table-column prop="douyinSettlement" label="渠道结算总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.douyinSettlement) }}</template>
          </el-table-column>
          <el-table-column prop="douyinRoi" label="渠道ROI" width="80" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.douyinRoi) }}</template>
          </el-table-column>
        </el-table-column>
        <el-table-column label="小红书推广" align="center">
          <el-table-column prop="xiaohongshuConsumption" label="渠道消费总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.xiaohongshuConsumption) }}</template>
          </el-table-column>
          <el-table-column prop="xiaohongshuResource" label="渠道资源总量" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.xiaohongshuResource) }}</template>
          </el-table-column>
          <el-table-column prop="xiaohongshuVerify" label="渠道核对订单总数" width="150" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.xiaohongshuVerify) }}</template>
          </el-table-column>
          <el-table-column prop="xiaohongshuDealAmount" label="渠道成交总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.xiaohongshuDealAmount) }}</template>
          </el-table-column>
          <el-table-column prop="xiaohongshuSettlement" label="渠道结算总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.xiaohongshuSettlement) }}</template>
          </el-table-column>
          <el-table-column prop="xiaohongshuRoi" label="渠道ROI" width="80" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.xiaohongshuRoi) }}</template>
          </el-table-column>
        </el-table-column>
        <el-table-column label="转介绍" align="center">
          <el-table-column prop="referralConsumption" label="渠道消费总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.referralConsumption) }}</template>
          </el-table-column>
          <el-table-column prop="referralResource" label="渠道资源总量" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.referralResource) }}</template>
          </el-table-column>
          <el-table-column prop="referralVerify" label="渠道核对订单总数" width="150" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.referralVerify) }}</template>
          </el-table-column>
          <el-table-column prop="referralDealAmount" label="渠道成交总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.referralDealAmount) }}</template>
          </el-table-column>
          <el-table-column prop="referralSettlement" label="渠道结算总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.referralSettlement) }}</template>
          </el-table-column>
          <el-table-column prop="referralRoi" label="渠道ROI" width="80" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.referralRoi) }}</template>
          </el-table-column>
        </el-table-column>
        <el-table-column label="二次推荐" align="center">
          <el-table-column prop="secondReferralConsumption" label="渠道消费总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.secondReferralConsumption) }}</template>
          </el-table-column>
          <el-table-column prop="secondReferralResource" label="渠道资源总量" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.secondReferralResource) }}</template>
          </el-table-column>
          <el-table-column prop="secondReferralVerify" label="渠道核对订单总数" width="150" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.secondReferralVerify) }}</template>
          </el-table-column>
          <el-table-column prop="secondReferralDealAmount" label="渠道成交总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.secondReferralDealAmount) }}</template>
          </el-table-column>
          <el-table-column prop="secondReferralSettlement" label="渠道结算总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.secondReferralSettlement) }}</template>
          </el-table-column>
          <el-table-column prop="secondReferralRoi" label="渠道ROI" width="80" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.secondReferralRoi) }}</template>
          </el-table-column>
        </el-table-column>
        <el-table-column label="其他推广" align="center">
          <el-table-column prop="otherConsumption" label="渠道消费总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.otherConsumption) }}</template>
          </el-table-column>
          <el-table-column prop="otherResource" label="渠道资源总量" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.otherResource) }}</template>
          </el-table-column>
          <el-table-column prop="otherVerify" label="渠道核对订单总数" width="150" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.otherVerify) }}</template>
          </el-table-column>
          <el-table-column prop="otherDealAmount" label="渠道成交总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.otherDealAmount) }}</template>
          </el-table-column>
          <el-table-column prop="otherSettlement" label="渠道结算总额" width="120" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.otherSettlement) }}</template>
          </el-table-column>
          <el-table-column prop="otherRoi" label="渠道ROI" width="80" align="center">
            <template slot-scope="{ row }">{{ formatNum(row.otherRoi) }}</template>
          </el-table-column>
        </el-table-column>
      </el-table>
    </el-card>
    <div class="pagination-wrapper">
      <el-pagination
        class="pagination"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import UtwlOrgSelect from '@/components/utwl-org-select.vue';

const ROI_CHANNEL_PREFIXES = ['baidu', 'douyin', 'xiaohongshu', 'referral', 'secondReferral', 'other'];

function getDefaultDateRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  return [
    `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, '0')}-${String(first.getDate()).padStart(2, '0')}`,
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  ];
}

function formatDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function parseLocalDate(value) {
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const [year, month, day] = String(value).split('-').map(Number);
  return new Date(year, month - 1, day);
}

function getWeekMonday(value) {
  const date = parseLocalDate(value);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.getFullYear(), date.getMonth(), diff);
}

function getDefaultDateList(statType) {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  switch (statType) {
    case 'year':
      return [String(now.getFullYear())];
    case 'month':
      return [`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`];
    case 'week': {
      return [formatDate(getWeekMonday(now))];
    }
    case 'day': {
      // 按日默认仅选中「今天」，便于从按周等切换后日期控件直接展示当前客户端日期（多选仍可在面板中选「最近7天」等）
      return [formatDate(now)];
    }
    default:
      return [formatDate(first)];
  }
}

export default {
  name: 'ROIAudit',
  components: {
    UtwlOrgSelect
  },
  data() {
    return {
      loading: false,
      queryLoading: false,
      exportLoading: false,
      tableData: [],
      tableRenderKey: 0,
      auditTotals: {
        consumption: 0,
        resourceCount: 0,
        verifyCount: 0,
        dealAmount: 0,
        settlementAmount: 0
      },
      paginationTotal: 0,
      currentPage: 1,
      pageSize: 10,
      searchForm: {
        statType: 'month',
        dateRange: getDefaultDateRange(),
        dateList: getDefaultDateList('month'),
        orgIds: []
      }
    };
  },
  computed: {
    pickerDateType() {
      // Element UI 2 仅支持 week（单周），不存在 weeks；误用 weeks 会导致无法弹出/无法选择
      const map = { year: 'year', month: 'month', week: 'week', day: 'dates' };
      return map[this.searchForm.statType] || 'month';
    },
    pickerDateFormat() {
      return this.searchForm.statType === 'week' ? 'yyyy 第 WW 周' : undefined;
    },
    pickerDateValueFormat() {
      const map = { year: 'yyyy', month: 'yyyy-MM', week: 'yyyy-MM-dd', day: 'yyyy-MM-dd' };
      return map[this.searchForm.statType] || 'yyyy-MM-dd';
    },
    pickerDatePlaceholder() {
      const map = { year: '选择年份', month: '选择月份', week: '选择周', day: '选择日期' };
      return map[this.searchForm.statType] || '选择';
    },
    pickerDateOptions() {
      const vm = this;
      if (this.searchForm.statType === 'year') {
        return {
          disabledDate(time) {
            return time.getFullYear() > new Date().getFullYear();
          },
          shortcuts: [
            { text: '今年', onClick(picker) {
              const y = new Date().getFullYear();
              picker.$emit('pick', `${y}`);
            }},
            { text: '去年', onClick(picker) {
              const y = new Date().getFullYear() - 1;
              picker.$emit('pick', `${y}`);
            }}
          ]
        };
      }
      if (this.searchForm.statType === 'month') {
        return {
          disabledDate(time) {
            const now = new Date();
            return time.getFullYear() > now.getFullYear()
              || (time.getFullYear() === now.getFullYear() && time.getMonth() > now.getMonth());
          },
          shortcuts: [
            { text: '本月', onClick(picker) {
              const now = new Date();
              const m = String(now.getMonth() + 1).padStart(2, '0');
              picker.$emit('pick', `${now.getFullYear()}-${m}`);
            }},
            { text: '上月', onClick(picker) {
              const now = new Date();
              const prev = new Date(now.getFullYear(), now.getMonth() - 1);
              const m = String(prev.getMonth() + 1).padStart(2, '0');
              picker.$emit('pick', `${prev.getFullYear()}-${m}`);
            }}
          ]
        };
      }
      if (this.searchForm.statType === 'week') {
        return {
          firstDayOfWeek: 1,
          disabledDate(time) {
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            return time.getTime() > today.getTime();
          },
          shortcuts: [
            { text: '本周', onClick(picker) {
              const now = new Date();
              const day = now.getDay();
              const diff = now.getDate() - day + (day === 0 ? -6 : 1);
              const monday = new Date(now);
              monday.setDate(diff);
              const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
              picker.$emit('pick', fmt(monday));
            }},
            { text: '上周', onClick(picker) {
              const now = new Date();
              const day = now.getDay();
              const diff = now.getDate() - day + (day === 0 ? -6 : 1);
              const monday = new Date(now);
              monday.setDate(diff - 7);
              const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
              picker.$emit('pick', fmt(monday));
            }}
          ]
        };
      }
      if (this.searchForm.statType === 'day') {
        const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
        return {
          disabledDate(time) {
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            return time.getTime() > today.getTime();
          },
          shortcuts: [
            { text: '今天', onClick(picker) {
              const now = new Date();
              picker.$emit('pick', [fmt(now)]);
            }},
            { text: '昨天', onClick(picker) {
              const d = new Date();
              d.setDate(d.getDate() - 1);
              picker.$emit('pick', [fmt(d)]);
            }},
            { text: '最近7天', onClick(picker) {
              const now = new Date();
              const arr = [];
              for (let i = 6; i >= 0; i--) {
                const d = new Date(now);
                d.setDate(d.getDate() - i);
                arr.push(fmt(d));
              }
              picker.$emit('pick', arr);
            }},
            { text: '本月至今', onClick(picker) {
              const now = new Date();
              const first = new Date(now.getFullYear(), now.getMonth(), 1);
              const arr = [];
              const curr = new Date(first);
              while (curr <= now) {
                arr.push(fmt(curr));
                curr.setDate(curr.getDate() + 1);
              }
              picker.$emit('pick', arr);
            }}
          ]
        };
      }
      return {};
    },
    pickerDateValue() {
      const list = this.searchForm.dateList;
      // type=week 时组件绑定值为单个日期字符串（当周任意一天），不能传数组
      if (['year', 'month', 'week'].includes(this.searchForm.statType)) {
        if (!list || list.length === 0) return null;
        return list[0];
      }
      if (!list || list.length === 0) return [];
      return list;
    },
    total() {
      return this.paginationTotal;
    },
    pagedTableData() {
      return this.tableData;
    }
  },
  watch: {
    'searchForm.statType'(newVal, oldVal) {
      if (oldVal && newVal !== oldVal) {
        this.searchForm.dateList = getDefaultDateList(newVal);
        this.syncDateRangeFromDateList();
      }
    }
  },
  mounted() {
    this.syncDateRangeFromDateList();
    this.loadData({ refreshStats: true });
  },
  methods: {
    handlePickerDateInput(val) {
      let arr;
      if (['year', 'month', 'week'].includes(this.searchForm.statType)) {
        arr = val != null && val !== '' ? [val] : [];
      } else {
        arr = Array.isArray(val) ? val : (val != null && val !== '' ? [val] : []);
      }
      this.searchForm.dateList = arr;
      this.syncDateRangeFromDateList();
    },
    syncDateRangeFromDateList() {
      const list = this.searchForm.dateList;
      if (!list || list.length === 0) {
        this.searchForm.dateRange = [];
        return;
      }
      switch (this.searchForm.statType) {
        case 'year': {
          const years = [...list].map(String).sort();
          const y1 = years[0];
          const y2 = years[years.length - 1];
          this.searchForm.dateRange = [`${y1}-01-01`, `${y2}-12-31`];
          break;
        }
        case 'month': {
          const months = [...list].sort();
          const [y2, m2] = months[months.length - 1].split('-').map(Number);
          const lastDay = new Date(y2, m2, 0).getDate();
          this.searchForm.dateRange = [`${months[0]}-01`, `${months[months.length - 1]}-${String(lastDay).padStart(2, '0')}`];
          const now = new Date();
          if (months.length === 1 && now.getFullYear() === y2 && now.getMonth() + 1 === m2) {
            const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            this.searchForm.dateRange[1] = today;
          }
          break;
        }
        case 'week': {
          const mondays = [...list].map(getWeekMonday).sort((a, b) => a.getTime() - b.getTime());
          const firstMonday = mondays[0];
          const lastMonday = mondays[mondays.length - 1];
          const sunday = new Date(lastMonday);
          sunday.setDate(lastMonday.getDate() + 6);
          this.searchForm.dateRange = [formatDate(firstMonday), formatDate(sunday)];
          break;
        }
        case 'day': {
          const dates = [...list].sort();
          this.searchForm.dateRange = [dates[0], dates[dates.length - 1]];
          break;
        }
        default:
          this.searchForm.dateRange = list.length > 0 ? [list[0], list[list.length - 1]] : [];
      }
    },
    getQueryParams() {
      const orgIds = (this.searchForm.orgIds || []).filter(id => id !== '__load_more__');
      return {
        statType: this.searchForm.statType,
        dateRange: this.searchForm.dateRange || [],
        orgIds
      };
    },
    buildCampusListPayload(page = this.currentPage, pageSize = this.pageSize) {
      const params = this.getQueryParams();
      const payload = {
        statType: params.statType,
        dateRange: params.dateRange,
        page,
        pageSize
      };
      if (params.orgIds && params.orgIds.length > 0) {
        payload.orgIds = JSON.stringify(params.orgIds);
      }
      return payload;
    },
    buildStatsPayload() {
      const params = this.getQueryParams();
      const payload = {
        statType: params.statType,
        dateRange: params.dateRange
      };
      if (params.orgIds && params.orgIds.length > 0) {
        payload.orgIds = JSON.stringify(params.orgIds);
      }
      return payload;
    },
    /**
     * 同一机构内，相同非空【城市名称】只保留一行（排序后最先出现的那条）。
     * 不同机构即使同城也会分别展示，避免接口返回多家机构却只显示一家的问题。
     */
    dedupeRowsByCityName(rows) {
      const seen = new Set();
      const out = [];
      for (const row of rows) {
        const city = (row.cityName || '').trim();
        if (!city) {
          out.push(row);
          continue;
        }
        const orgKey = (row.orgId || row.orgName || '').trim() || '_';
        const key = `${orgKey}::${city}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(row);
      }
      return out;
    },
    async fetchRoiRows(page = this.currentPage, pageSize = this.pageSize) {
      const res = await api.post('/roi/audit/list', this.buildCampusListPayload(page, pageSize));
      if (!res.data.success) {
        return { success: false, rows: [], total: 0 };
      }
      const data = res.data.data || {};
      const list = data.list || [];
      const rows = list.map(c => this.campusToRoiRow(c));
      rows.sort((a, b) => {
        const o = (a.orgName || '').localeCompare(b.orgName || '');
        if (o !== 0) return o;
        return (a.promotionId || '').localeCompare(b.promotionId || '');
      });
      const total = data.total != null ? Number(data.total) : rows.length;
      return {
        success: true,
        rows,
        total: Number.isFinite(total) ? total : 0
      };
    },
    async fetchRoiStats() {
      const res = await api.post('/roi/audit/stats', this.buildStatsPayload());
      if (!res.data.success) {
        return this.emptyAuditTotals();
      }
      return this.normalizeAuditTotals(res.data.data);
    },
    async loadData({ refreshStats = false } = {}) {
      this.loading = true;
      try {
        let result;
        let totals = this.auditTotals;
        if (refreshStats) {
          [result, totals] = await Promise.all([
            this.fetchRoiRows(),
            this.fetchRoiStats()
          ]);
        } else {
          result = await this.fetchRoiRows();
        }
        if (!result.success) {
          this.tableData = [];
          this.applyAuditTotals(this.emptyAuditTotals());
          this.paginationTotal = 0;
          this.currentPage = 1;
          return;
        }
        this.paginationTotal = result.total;
        if (refreshStats) {
          this.applyAuditTotals(totals);
        }
        const maxPage = Math.max(1, Math.ceil(this.paginationTotal / this.pageSize));
        if (this.currentPage > maxPage) {
          this.currentPage = maxPage;
          result = await this.fetchRoiRows();
          if (!result.success) {
            this.tableData = [];
            this.applyAuditTotals(this.emptyAuditTotals());
            this.paginationTotal = 0;
            return;
          }
          this.paginationTotal = result.total;
        }
        this.tableData = result.rows;
      } catch (e) {
        this.$message.error('加载数据失败');
        this.tableData = [];
        this.applyAuditTotals(this.emptyAuditTotals());
        this.paginationTotal = 0;
        this.currentPage = 1;
      } finally {
        this.loading = false;
      }
    },
    campusToRoiRow(campus) {
      const promotionId = campus.promotionId || '';
      const pickNumber = (key, fallback = 0) => {
        if (campus[key] === null || campus[key] === undefined || campus[key] === '') return fallback;
        const n = Number(campus[key]);
        return Number.isFinite(n) ? n : fallback;
      };
      const pickRoi = (key) => {
        if (campus[key] === null || campus[key] === undefined || campus[key] === '') return null;
        const n = Number(campus[key]);
        return Number.isFinite(n) ? n : null;
      };
      const row = {
        orgId: campus.orgId || '',
        promotionId,
        cityName: (campus.cityName != null && campus.cityName !== '') ? String(campus.cityName).trim() : '',
        orgName: campus.orgName || '',
        cooperationStartDate: campus.cooperationStartDate || '',
        consumption: pickNumber('consumption'),
        resourceCount: pickNumber('resourceCount'),
        verifyCount: pickNumber('verifyCount'),
        enrollRate: campus.enrollRate || '0%',
        dealAmount: pickNumber('dealAmount'),
        settlementAmount: pickNumber('settlementAmount'),
        perOrder: pickNumber('perOrder'),
        totalPromotionRoi: pickRoi('totalPromotionRoi'),
        baiduConsumption: pickNumber('baiduConsumption'),
        baiduResource: pickNumber('baiduResource'),
        baiduVerify: pickNumber('baiduVerify'),
        baiduDealAmount: pickNumber('baiduDealAmount'),
        baiduSettlement: pickNumber('baiduSettlement'),
        baiduRoi: pickRoi('baiduRoi'),
        douyinConsumption: pickNumber('douyinConsumption'),
        douyinResource: pickNumber('douyinResource'),
        douyinVerify: pickNumber('douyinVerify'),
        douyinDealAmount: pickNumber('douyinDealAmount'),
        douyinSettlement: pickNumber('douyinSettlement'),
        douyinRoi: pickRoi('douyinRoi'),
        xiaohongshuConsumption: pickNumber('xiaohongshuConsumption'),
        xiaohongshuResource: pickNumber('xiaohongshuResource'),
        xiaohongshuVerify: pickNumber('xiaohongshuVerify'),
        xiaohongshuDealAmount: pickNumber('xiaohongshuDealAmount'),
        xiaohongshuSettlement: pickNumber('xiaohongshuSettlement'),
        xiaohongshuRoi: pickRoi('xiaohongshuRoi'),
        referralConsumption: pickNumber('referralConsumption'),
        referralResource: pickNumber('referralResource'),
        referralVerify: pickNumber('referralVerify'),
        referralDealAmount: pickNumber('referralDealAmount'),
        referralSettlement: pickNumber('referralSettlement'),
        referralRoi: pickRoi('referralRoi'),
        secondReferralConsumption: pickNumber('secondReferralConsumption'),
        secondReferralResource: pickNumber('secondReferralResource'),
        secondReferralVerify: pickNumber('secondReferralVerify'),
        secondReferralDealAmount: pickNumber('secondReferralDealAmount'),
        secondReferralSettlement: pickNumber('secondReferralSettlement'),
        secondReferralRoi: pickRoi('secondReferralRoi'),
        otherConsumption: pickNumber('otherConsumption'),
        otherResource: pickNumber('otherResource'),
        otherVerify: pickNumber('otherVerify'),
        otherDealAmount: pickNumber('otherDealAmount'),
        otherSettlement: pickNumber('otherSettlement'),
        otherRoi: pickRoi('otherRoi')
      };
      this.syncRowParentTotals(row);
      row.totalPromotionRoi = this.computeTotalPromotionRoi(row);
      return row;
    },
    sumChannelFields(row, suffix) {
      return ROI_CHANNEL_PREFIXES.reduce((sum, prefix) => {
        const n = Number(row[`${prefix}${suffix}`]);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0);
    },
    syncRowParentTotals(row) {
      row.consumption = this.sumChannelFields(row, 'Consumption');
      row.resourceCount = this.sumChannelFields(row, 'Resource');
      row.verifyCount = this.sumChannelFields(row, 'Verify');
      row.dealAmount = this.sumChannelFields(row, 'DealAmount');
      row.settlementAmount = this.sumChannelFields(row, 'Settlement');
      row.perOrder = row.verifyCount > 0 ? row.settlementAmount / row.verifyCount : 0;
      ROI_CHANNEL_PREFIXES.forEach((prefix) => {
        row[`${prefix}Roi`] = this.computeRoi(row[`${prefix}Settlement`], row[`${prefix}Consumption`]);
      });
    },
    emptyAuditTotals() {
      return {
        consumption: 0,
        resourceCount: 0,
        verifyCount: 0,
        dealAmount: 0,
        settlementAmount: 0
      };
    },
    normalizeAuditTotals(totals) {
      const source = totals || {};
      const out = this.emptyAuditTotals();
      Object.keys(out).forEach((key) => {
        const n = Number(source[key]);
        out[key] = Number.isFinite(n) ? n : 0;
      });
      return out;
    },
    applyAuditTotals(totals) {
      this.auditTotals = this.normalizeAuditTotals(totals);
      this.tableRenderKey += 1;
      this.$nextTick(() => {
        if (this.$refs.roiAuditTable && this.$refs.roiAuditTable.doLayout) {
          this.$refs.roiAuditTable.doLayout();
        }
      });
    },
    /** 总ROI：结算总额 / 总消费 - 1，与帮助说明中 ROI 公式一致 */
    computeTotalPromotionRoi(row) {
      return this.computeRoi(row.settlementAmount, row.consumption);
    },
    computeRoi(settlement, consumption) {
      const c = Number(consumption);
      const s = Number(settlement);
      if (!Number.isFinite(c) || c <= 0) return null;
      if (!Number.isFinite(s) || s <= 0) return null;
      return s / c - 1;
    },
    async handleQuery() {
      this.queryLoading = true;
      try {
        this.currentPage = 1;
        await this.loadData({ refreshStats: true });
      } finally {
        this.queryLoading = false;
      }
    },
    handleReset() {
      this.searchForm = {
        statType: 'month',
        dateRange: getDefaultDateRange(),
        dateList: getDefaultDateList('month'),
        orgIds: []
      };
      this.handleQuery();
    },
    async handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      await this.loadData();
    },
    async handleCurrentChange(page) {
      this.currentPage = page;
      await this.loadData();
    },
    getOrgRowKey(row) {
      const id = row && row.orgId != null ? String(row.orgId).trim() : '';
      if (id) return `id:${id}`;
      const name = row && row.orgName != null ? String(row.orgName).trim() : '';
      return `name:${name}`;
    },
    indexMethod(index) {
      const data = this.pagedTableData || [];
      const seen = new Set();
      let orgIndexInPage = 0;
      for (let i = 0; i <= index && i < data.length; i++) {
        const key = this.getOrgRowKey(data[i]);
        if (seen.has(key)) continue;
        seen.add(key);
        orgIndexInPage += 1;
      }
      return (this.currentPage - 1) * this.pageSize + orgIndexInPage;
    },
    async handleExport() {
      this.exportLoading = true;
      try {
        if (this.paginationTotal > 1000) {
          this.$message.warning('单次导出不能超过1000个机构品牌，请缩小查询范围');
          return;
        }
        const result = await this.fetchRoiRows(1, Math.max(this.paginationTotal, 1));
        if (!result.success) {
          this.$message.warning('没有符合条件的数据可导出');
          return;
        }
        const data = result.rows;
        if (data.length === 0) {
          this.$message.warning('没有符合条件的数据可导出');
          return;
        }
        if (data.length > 1000) {
          this.$message.warning('单次导出不能超过1000条数据，请缩小查询范围');
          return;
        }
        // 导出使用当前查询条件下的机构品牌分页结果，最多 1000 个机构品牌。
        this.exportToExcel(data);
      } catch (e) {
        this.$message.error('导出失败');
      } finally {
        this.exportLoading = false;
      }
    },
    exportToExcel(data) {
      const XLSX = require('xlsx');
      const headers = ['机构品牌', '推广ID', '城市名称', '消费总额', '资源总量', '核对订单总数', '报名率', '成交总额', '结算总额', '单笔', '总ROI',
        '百度消费', '百度资源', '百度核对', '百度成交金额', '百度结算', '百度ROI',
        '抖音消费', '抖音资源', '抖音核对', '抖音成交金额', '抖音结算', '抖音ROI',
        '小红书消费', '小红书资源', '小红书核对', '小红书成交金额', '小红书结算', '小红书ROI',
        '转介绍消费', '转介绍资源', '转介绍核对', '转介绍成交金额', '转介绍结算', '转介绍ROI',
        '二次推荐消费', '二次推荐资源', '二次推荐核对', '二次推荐成交金额', '二次推荐结算', '二次推荐ROI',
        '其他消费', '其他资源', '其他核对', '其他成交金额', '其他结算', '其他ROI'];
      const exportData = data.map((row, index) => ({
        '序号': index + 1,
        '机构品牌': row.orgName || '',
        '推广ID': row.promotionId || '',
        '城市名称': row.cityName || '',
        '消费总额': row.consumption || '',
        '资源总量': row.resourceCount || '',
        '核对订单总数': row.verifyCount || '',
        '报名率': row.enrollRate || '',
        '成交总额': row.dealAmount || '',
        '结算总额': row.settlementAmount || '',
        '单笔': row.perOrder || '',
        '总ROI': row.totalPromotionRoi == null ? '' : row.totalPromotionRoi,
        '百度消费': row.baiduConsumption || '',
        '百度资源': row.baiduResource || '',
        '百度核对': row.baiduVerify || '',
        '百度成交金额': row.baiduDealAmount || '',
        '百度结算': row.baiduSettlement || '',
        '百度ROI': row.baiduRoi || '',
        '抖音消费': row.douyinConsumption || '',
        '抖音资源': row.douyinResource || '',
        '抖音核对': row.douyinVerify || '',
        '抖音成交金额': row.douyinDealAmount || '',
        '抖音结算': row.douyinSettlement || '',
        '抖音ROI': row.douyinRoi || '',
        '小红书消费': row.xiaohongshuConsumption || '',
        '小红书资源': row.xiaohongshuResource || '',
        '小红书核对': row.xiaohongshuVerify || '',
        '小红书成交金额': row.xiaohongshuDealAmount || '',
        '小红书结算': row.xiaohongshuSettlement || '',
        '小红书ROI': row.xiaohongshuRoi || '',
        '转介绍消费': row.referralConsumption || '',
        '转介绍资源': row.referralResource || '',
        '转介绍核对': row.referralVerify || '',
        '转介绍成交金额': row.referralDealAmount || '',
        '转介绍结算': row.referralSettlement || '',
        '转介绍ROI': row.referralRoi || '',
        '二次推荐消费': row.secondReferralConsumption || '',
        '二次推荐资源': row.secondReferralResource || '',
        '二次推荐核对': row.secondReferralVerify || '',
        '二次推荐成交金额': row.secondReferralDealAmount || '',
        '二次推荐结算': row.secondReferralSettlement || '',
        '二次推荐ROI': row.secondReferralRoi || '',
        '其他消费': row.otherConsumption || '',
        '其他资源': row.otherResource || '',
        '其他核对': row.otherVerify || '',
        '其他成交金额': row.otherDealAmount || '',
        '其他结算': row.otherSettlement || '',
        '其他ROI': row.otherRoi || ''
      }));
      const ws = XLSX.utils.json_to_sheet(exportData, { header: ['序号', ...headers] });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'ROI监控');
      const now = new Date();
      const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
      const fileName = `ROI监控${data.length}-${dateStr}.xlsx`;
      XLSX.writeFile(wb, fileName);
      this.$message.success(`成功导出 ${data.length} 条数据`);
    },
    formatNum(val) {
      if (val == null || val === '' || val === '-') return '-';
      const n = Number(val);
      if (Number.isNaN(n)) return val;
      if (Number.isInteger(n)) return String(n);
      return n.toFixed(2);
    },
    /** 相邻行「机构品牌」相同时纵向合并（数据已按机构品牌、推广ID 排序） */
    orgNameSpanMethod({ row, column, rowIndex }) {
      const shouldMergeOrgCell = column.property === 'orgName' || column.type === 'index';
      if (!shouldMergeOrgCell) {
        return [1, 1];
      }
      const data = this.pagedTableData;
      if (!data || rowIndex >= data.length) return [1, 1];
      const name = this.getOrgRowKey(row);
      const prev = rowIndex > 0 ? data[rowIndex - 1] : null;
      if (prev && this.getOrgRowKey(prev) === name) {
        return [0, 0];
      }
      let rowspan = 1;
      for (let i = rowIndex + 1; i < data.length; i++) {
        if (this.getOrgRowKey(data[i]) === name) rowspan += 1;
        else break;
      }
      return [rowspan, 1];
    },
    getCellClassName({ row, column }) {
      const prop = column.property || '';
      if (/^(baidu|douyin|xiaohongshu|referral|secondReferral|other)(Consumption|Resource|Verify|DealAmount|Settlement)$/.test(prop)) {
        if (prop.endsWith('Consumption')) return 'roi-col-consumption';
        if (prop.endsWith('Resource')) return 'roi-col-resource';
        if (prop.endsWith('Verify')) return 'roi-col-verify';
        if (prop.endsWith('DealAmount')) return 'roi-col-deal';
        if (prop.endsWith('Settlement')) return 'roi-col-settlement';
      }
      return '';
    },
    getCellStyle({ row, column }) {
      const prop = column.property || '';
      if (!prop || ['orgName', 'promotionId', 'cityName'].includes(prop)) {
        return {};
      }
      const rawValue = row ? row[prop] : null;
      if (rawValue == null || rawValue === '' || rawValue === '-') {
        return {};
      }
      const value = typeof rawValue === 'string'
        ? rawValue.trim().replace(/,/g, '').replace(/%$/, '')
        : rawValue;
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue)) {
        return {};
      }
      return {
        color: numberValue === 0 ? '#492d22' : '#f00',
        fontSize:numberValue === 0 ? '13px' : '15px',
      };
    },
    getHeaderCellStyle({ row, column, rowIndex }) {
      if (rowIndex === 0 && column.label) {
        const colors = {
          '百度推广': '#53a8ff',
          '抖音推广': '#66b1ff',
          '小红书推广': '#79bbff',
          '转介绍': '#8cc5ff',
          '二次推荐': '#a0cfff',
          '其他推广': '#b3d8ff'
        };
        if (colors[column.label]) {
          return { background: colors[column.label] };
        }
      }
      if (rowIndex === 1 && column.label) {
        const subColors = {
          '渠道消费总额': '#a0cfff',
          '渠道资源总量': '#8cc5ff',
          '渠道核对订单总数': '#79bbff',
          '渠道成交总额': '#66b1ff',
          '渠道结算总额': '#53a8ff'
        };
        if (subColors[column.label]) {
          return { background: subColors[column.label] };
        }
      }
      return { background: '#f5f7fa' };
    },
    getHeaderCellClassName({ row, column, rowIndex }) {
      if (rowIndex === 0 && column.label) {
        const classMap = {
          '百度推广': 'roi-header-baidu',
          '抖音推广': 'roi-header-douyin',
          '小红书推广': 'roi-header-xiaohongshu',
          '转介绍': 'roi-header-referral',
          '二次推荐': 'roi-header-second-referral',
          '其他推广': 'roi-header-other'
        };
        return classMap[column.label] || '';
      }
      if (rowIndex === 1 && column.label) {
        const subClassMap = {
          '渠道消费总额': 'roi-sub-consumption',
          '渠道资源总量': 'roi-sub-resource',
          '渠道核对订单总数': 'roi-sub-verify',
          '渠道成交总额': 'roi-sub-deal',
          '渠道结算总额': 'roi-sub-settlement'
        };
        return subClassMap[column.label] || '';
      }
      return '';
    },
  }
};
</script>

<style scoped>
.roi-audit-page {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.filter-bar {
  flex-shrink: 0;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.filter-form {
  width: 100%;
}

.filter-form >>> .el-form-item {
  margin-bottom: 0;
}

.filter-grid {
  display: flex;
  flex-wrap: wrap;
  row-gap: 12px;
}

.filter-stat-radios {
  width: 100%;
  white-space: nowrap;
}

.filter-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 40px;
}

.filter-actions >>> .el-button + .el-button {
  margin-left: 0;
}

.filter-date-picker {
  width: 100%;
}

.filter-date-picker >>> .el-range-separator {
  width: 7%;
}

.filter-org-select {
  width: 100%;
}

.roi-help-icon {
  margin-left: 8px;
  font-size: 18px;
  color: #53a8ff;
  cursor: help;
  vertical-align: middle;
}

.roi-help-icon:hover {
  color: #606266;
}

.roi-help-content p {
  margin: 6px 0;
  line-height: 1.6;
  font-size: 13px;
}

.table-card {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.table-card >>> .el-card__body {
  flex: 1;
  padding: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* 表格占满卡片剩余空间，交给 Element UI 表格内部滚动同步固定列。 */
.roi-audit-table {
  flex: 1;
  min-height: 0;
}

.roi-total-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  line-height: 1.2;
  white-space: nowrap;
}

.roi-total-value {
  color: #00c853;
  font-weight: 700;
  white-space: nowrap;
}

.roi-audit-table >>> th .cell {
  white-space: nowrap;
}

.roi-audit-table >>> td.org-name-column .cell {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  line-height: 20px;
}

.org-name-text {
  display: block;
  white-space: normal;
  word-break: break-all;
  overflow-wrap: anywhere;
}

.pagination-wrapper {
  position: static !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  z-index: auto !important;
  flex-shrink: 0;
  height: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
  border-top: 1px solid #ebeef5;
  box-shadow: none;
}

.roi-audit-table >>> .el-table__body td.cell-green {
  background-color: #f0f9eb !important;
  color: #67c23a;
}

.roi-audit-table >>> .el-table__body td.cell-yellow {
  background-color: #fdf6ec !important;
  color: #e6a23c;
}

/* 推广渠道一级表头背景色（覆盖全局 .el-table th 的 !important） */
.roi-audit-table >>> th.roi-header-baidu {
  background-color: #53a8ff !important;
}
.roi-audit-table >>> th.roi-header-douyin {
  background-color: #66b1ff !important;
}
.roi-audit-table >>> th.roi-header-xiaohongshu {
  background-color: #79bbff !important;
}
.roi-audit-table >>> th.roi-header-referral {
  background-color: #8cc5ff !important;
}
.roi-audit-table >>> th.roi-header-second-referral {
  background-color: #a0cfff !important;
}
.roi-audit-table >>> th.roi-header-other {
  background-color: #b3d8ff !important;
}

/* 二级表头背景色（反转后） */
.roi-audit-table >>> th.roi-sub-consumption {
  background-color: #d9ecff !important;
}
.roi-audit-table >>> th.roi-sub-resource {
  background-color: #d9ecff !important;
}
.roi-audit-table >>> th.roi-sub-verify {
  background-color: #d9ecff !important;
}
.roi-audit-table >>> th.roi-sub-deal {
  background-color: #d9ecff !important;
}
.roi-audit-table >>> th.roi-sub-settlement {
  background-color: #d9ecff !important;
}

/* 表格列整列背景色（消费、资源、核对、成交金额、结算，反转后） */
/* .roi-audit-table >>> td.roi-col-consumption {
  background-color: #a0cfff !important;
}
.roi-audit-table >>> td.roi-col-resource {
  background-color: #a0cfff !important;
}
.roi-audit-table >>> td.roi-col-verify {
  background-color: #a0cfff !important;
}
.roi-audit-table >>> td.roi-col-deal {
  background-color: #a0cfff !important;
}
.roi-audit-table >>> td.roi-col-settlement {
  background-color: #a0cfff !important;
} */
/* 表格行 hover 时保持列背景色 */
/* .roi-audit-table >>> .el-table__body tr:hover > td.roi-col-consumption {
  background-color: #a0cfff !important;
}
.roi-audit-table >>> .el-table__body tr:hover > td.roi-col-resource {
  background-color: #8cc5ff !important;
}
.roi-audit-table >>> .el-table__body tr:hover > td.roi-col-verify {
  background-color: #79bbff !important;
}
.roi-audit-table >>> .el-table__body tr:hover > td.roi-col-deal {
  background-color: #66b1ff !important;
}
.roi-audit-table >>> .el-table__body tr:hover > td.roi-col-settlement {
  background-color: #53a8ff !important;
} */
/* 斑马纹行时保持列背景色 */
/* .roi-audit-table >>> .el-table__body tr.el-table__row--striped td.roi-col-consumption {
  background-color: #a0cfff !important;
}
.roi-audit-table >>> .el-table__body tr.el-table__row--striped td.roi-col-resource {
  background-color: #8cc5ff !important;
}
.roi-audit-table >>> .el-table__body tr.el-table__row--striped td.roi-col-verify {
  background-color: #79bbff !important;
}
.roi-audit-table >>> .el-table__body tr.el-table__row--striped td.roi-col-deal {
  background-color: #66b1ff !important;
}
.roi-audit-table >>> .el-table__body tr.el-table__row--striped td.roi-col-settlement {
  background-color: #53a8ff !important;
} */
</style>
